import React, { useMemo, useState } from 'react';
import { Card, Empty, Segmented, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { BqSegmentRow } from '@/types';
import { dayLabel, fmtNum, fmtPct, fmtUsd } from './bqMath';
import { WholeGameTag } from './WholeGameTag';

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'country', label: 'Country' },
  { value: 'app_version', label: 'App version' },
  { value: 'ram_band', label: 'RAM' },
  { value: 'perf_tier', label: 'Perf tier' },
  { value: 'install_source', label: 'Install source' },
  { value: 'ft_source', label: 'First-touch source' },
];

const byNumber =
  (key: keyof BqSegmentRow) =>
  (a: BqSegmentRow, b: BqSegmentRow): number =>
    ((a[key] as number | null) ?? -1) - ((b[key] as number | null) ?? -1);

const COLUMNS: ColumnsType<BqSegmentRow> = [
  { title: 'Segment', dataIndex: 'segment', key: 'segment' },
  {
    title: 'New players',
    dataIndex: 'newUsers',
    key: 'newUsers',
    align: 'right',
    sorter: byNumber('newUsers'),
    defaultSortOrder: 'descend',
    render: (v: number) => fmtNum(v),
  },
  {
    title: 'D1 retention',
    dataIndex: 'd1Retention',
    key: 'd1Retention',
    align: 'right',
    sorter: byNumber('d1Retention'),
    render: fmtPct,
  },
  {
    title: 'Uninstalled same day',
    dataIndex: 'uninstallRate',
    key: 'uninstallRate',
    align: 'right',
    sorter: byNumber('uninstallRate'),
    render: fmtPct,
  },
  {
    title: 'FTUE done',
    dataIndex: 'ftueRate',
    key: 'ftueRate',
    align: 'right',
    sorter: byNumber('ftueRate'),
    render: fmtPct,
  },
  {
    title: 'DAU',
    dataIndex: 'dau',
    key: 'dau',
    align: 'right',
    sorter: byNumber('dau'),
    render: (v: number) => fmtNum(v),
  },
  {
    title: 'Payer rate',
    dataIndex: 'payerRate',
    key: 'payerRate',
    align: 'right',
    sorter: byNumber('payerRate'),
    render: fmtPct,
  },
  {
    title: 'ARPDAU',
    dataIndex: 'arpdauUsd',
    key: 'arpdauUsd',
    align: 'right',
    sorter: byNumber('arpdauUsd'),
    render: (v: number | null) => fmtUsd(v, 4),
  },
];

/** New-player and activity KPIs per segment, latest day whose D1 is known; always the whole game. */
export const BqSegmentsTable: React.FC<{ segments: BqSegmentRow[]; wholeGame?: boolean }> = ({
  segments,
  wholeGame,
}) => {
  const available = DIMENSIONS.filter((d) => segments.some((s) => s.dimension === d.value));
  const [dimension, setDimension] = useState<string>(available[0]?.value ?? 'country');
  const rows = useMemo(
    () => segments.filter((s) => s.dimension === dimension),
    [segments, dimension]
  );
  const day = segments[0]?.date;

  return (
    <Card
      size="small"
      title={
        <>
          {day ? `Segments · installs of ${dayLabel(day)} (100+ new players)` : 'Segments'}
          <WholeGameTag show={wholeGame} />
        </>
      }
      extra={
        available.length > 0 && (
          <Segmented
            size="small"
            options={available}
            value={dimension}
            onChange={(v) => setDimension(String(v))}
          />
        )
      }
    >
      {rows.length === 0 ? (
        <Empty description="No segment with D1 known yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          size="small"
          rowKey={(r) => `${r.dimension}|${r.segment}`}
          columns={COLUMNS}
          dataSource={rows}
          pagination={rows.length > 15 ? { pageSize: 15, size: 'small' } : false}
          scroll={{ x: true }}
        />
      )}
    </Card>
  );
};
